import { useEffect } from "react";
import NotInterestedIcon from "@mui/icons-material/NotInterested";
import LinearProgressWithLabel from "../components/LinearProgressWIthLabel";
import { sortRequirementsByWeight } from "../helpers/helpers";
import { useTranslation } from "react-i18next";
import {
  Box,
  Typography,
  Slider,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  Divider,
  TextField,
  List,
  ListItem,
  FormControl,
} from "@mui/material";

export default function DisplayQuestion({
  setQuestion,
  questionData,
  index,
  percentage,
  setPercentage,
}) {
  const { t } = useTranslation();
  const sortedRequirementsWithAnswers = [
    ...(questionData?.requirementsWithAnswers || []),
  ].sort((a, b) => sortRequirementsByWeight(a.requirement, b.requirement));

  const handleRequirementChange = (requirementID, field, value) => {
    setQuestion((prev) => ({
      ...prev,
      requirementsWithAnswers: prev.requirementsWithAnswers.map((item) => {
        if (item.requirement.id !== requirementID) return item;
        return {
          ...item,
          answer: {
            ...item.answer,
            [field]: value,
          },
        };
      }),
    }));
  };

  const handleAnswerInfoChange = (field, value) => {
    const firstRequirementId =
      questionData.requirementsWithAnswers[0]?.requirement.id;

    setQuestion((prev) => ({
      ...prev,
      answerInfo: {
        ...prev.answerInfo,
        [field]: value,
      },
      requirementsWithAnswers: prev.requirementsWithAnswers.map((item) => {
        if (item.requirement.id !== firstRequirementId) return item;
        return {
          ...item,
          answer: {
            ...item.answer,
            [field]: value,
          },
        };
      }),
    }));
  };

  const toggleIrrelevant = (requirementId, newValue) => {
    handleRequirementChange(requirementId, "irrelevant", newValue);
    if (newValue) {
      handleRequirementChange(requirementId, "fulfilled", null);
      handleRequirementChange(requirementId, "selectedLevel", null);
    }
  };

  const handleSliderChange = (requirementID, newValue) => {
    handleRequirementChange(requirementID, "selectedLevel", Number(newValue));
    handleRequirementChange(requirementID, "fulfilled", true);
  };

  const handleRadioChange = (requirementID, selected) => {
    if (selected === t("fulfilled")) {
      handleRequirementChange(requirementID, "fulfilled", true);
      handleRequirementChange(requirementID, "selectedLevel", 0);
      handleRequirementChange(requirementID, "irrelevant", false);
    } else if (selected === t("notFulfilled")) {
      handleRequirementChange(requirementID, "fulfilled", false);
      handleRequirementChange(requirementID, "selectedLevel", null);
      handleRequirementChange(requirementID, "irrelevant", false);
    } else if (selected === t("notRelevant")) {
      handleRequirementChange(requirementID, "fulfilled", null);
      handleRequirementChange(requirementID, "selectedLevel", null);
      handleRequirementChange(requirementID, "irrelevant", true);
    }
  };

  const calculatePercentage = () => {
    let sum = 0;
    let total = 0;

    questionData.requirementsWithAnswers.forEach(({ requirement, answer }) => {
      if (!answer.irrelevant) {
        total += Number(requirement.weight);
        if (requirement.gradations) {
          if (
            answer.selectedLevel !== null &&
            answer.selectedLevel !== undefined
          ) {
            const maxLevel =
              (requirement.recommendations?.length || 1) - 1 || 1;
            sum += (requirement.weight * answer.selectedLevel) / maxLevel;
          }
        } else if (answer.fulfilled) {
          sum += Number(requirement.weight);
        }
      }
    });

    setPercentage(total > 0 ? (sum / total) * 100 : 0);
  };

  useEffect(() => {
    calculatePercentage();
  }, [questionData]);

  const getRadioValueForRequirement = (requirementID) => {
    const item = questionData.requirementsWithAnswers.find(
      (entry) => entry.requirement.id === requirementID
    );
    if (!item || !item.answer) return "";
    const { fulfilled, irrelevant } = item.answer;
    if (irrelevant) return t("notRelevant");
    if (fulfilled === true) return t("fulfilled");
    if (fulfilled === false) return t("notFulfilled");
    return "";
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: 1, mb: 5 }}>
      {/* Frageüberschrift */}
      <Box sx={{ display: "flex", flexDirection: "row" }}>
        <Typography
          sx={{ fontWeight: "bold", textDecoration: "underline", fontSize: 16 }}
        >
          {`${t("question")} Nr. ${index + 1}`}
        </Typography>
      </Box>

      {/* LinearProgressWithLabel */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "flex-end",
          mb: 2,
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            borderBottom: 1,
            borderColor: "#ccc",
          }}
        >
          <Typography sx={{ userSelect: "none" }}>
            {t("fulfilledPercent")}
          </Typography>
          <LinearProgressWithLabel progressValue={percentage} />
        </Box>
      </Box>

      {/* Hauptbereich */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          mt: 2,
        }}
      >
        {/* Linke Seite: Frage, Kontaktperson, Kommentar */}
        <Box sx={{ display: "flex", flexDirection: "column", minWidth: 1 / 2 }}>
          <Typography
            sx={{ maxWidth: 1000, fontSize: 16, ml: 4, whiteSpace: "pre-line" }}
          >
            {questionData?.question.text}
          </Typography>

          <Box
            sx={{
              flexDirection: "column",
              display: "flex",
              justifyContent: "center",
              width: 1,
              mt: 5,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "row", ml: 2 }}>
              <TextField
                color="secondary"
                InputLabelProps={{ shrink: true }}
                label={t("contactPerson")}
                variant="outlined"
                size="small"
                sx={{ width: 1 }}
                value={questionData?.answerInfo?.contactPerson || ""}
                onChange={(e) =>
                  handleAnswerInfoChange("contactPerson", e.target.value)
                }
              />
            </Box>
            <TextField
              color="secondary"
              InputLabelProps={{ shrink: true }}
              label={t("comments")}
              variant="outlined"
              size="small"
              multiline
              minRows={5}
              maxRows={5}
              sx={{ ml: 2, mt: 2 }}
              value={questionData?.answerInfo?.comment || ""}
              onChange={(e) => handleAnswerInfoChange("comment", e.target.value)}
            />
          </Box>
        </Box>

        {/* Rechte Seite: Requirements */}
        {questionData?.requirementsWithAnswers?.length > 0 && (
          <Box
            sx={{
              borderLeft: 1,
              pl: 3,
              ml: 3,
              borderColor: "#ccc",
              minWidth: 1 / 2,
              overflow: "visible",
            }}
          >
            <Typography
              sx={{
                fontSize: 17,
                textDecoration: "underline",
                whiteSpace: "nowrap",
                userSelect: "none",
              }}
            >
              {`${t("requirements")}:`}
            </Typography>
            <List sx={{ ml: -1, pb: 0, width: "100%" }}>
              {sortedRequirementsWithAnswers.map(
                ({ requirement, answer }, index) => (
                  <ListItem
                    sx={{
                      display: "flex",
                      pl: 0.5,
                      py: 0,
                      flexDirection: "row",
                      mt: index === 0 ? 0 : 0.5,
                      pt: index === 0 ? 0 : 0.5,
                      borderTop: index !== 0 ? 1 : 0,
                      borderColor: "#dddddd",
                    }}
                    key={requirement.id}
                  >
                    {/* Linke Box: Slider / Radio + Checkbox */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "25%",
                        borderRight: 1,
                        borderColor: "#ccc",
                        mr: 1,
                        pr: 1,
                      }}
                    >
                      {requirement.gradations ? (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            width: 1,
                          }}
                        >
                          <Slider
                            size="small"
                            sx={{
                              width: "90%",
                              ml: 0.6,
                              mb: 1.25,
                              "& .MuiSlider-markLabel": {
                                marginTop: -1.2,
                                fontSize: 12,
                                userSelect: "none",
                              },
                            }}
                            valueLabelDisplay="auto"
                            valueLabelFormat={(value) => String(value)}
                            value={answer?.selectedLevel || 0}
                            disabled={answer?.irrelevant}
                            step={1}
                            marks={
                              requirement.recommendations?.map((_, idx) => ({
                                value: idx,
                                label: String(idx),
                              })) || [{ value: 0, label: "0" }]
                            }
                            min={0}
                            max={(requirement.recommendations?.length || 1) - 1}
                            onChange={(e, val) =>
                              handleSliderChange(requirement.id, val)
                            }
                          />
                          <Checkbox
                            size="small"
                            checked={answer?.irrelevant || false}
                            sx={{
                              color: "#dddddd",
                              "&.Mui-checked": { color: "#000000" },
                              height: 30,
                              width: 30,
                            }}
                            icon={<NotInterestedIcon />}
                            checkedIcon={<NotInterestedIcon />}
                            onClick={(e) =>
                              toggleIrrelevant(requirement.id, e.target.checked)
                            }
                          />
                        </Box>
                      ) : (
                        <FormControl>
                          <RadioGroup
                            value={getRadioValueForRequirement(requirement.id)}
                            onChange={(e) =>
                              handleRadioChange(requirement.id, e.target.value)
                            }
                            name="radio-buttons-group"
                          >
                            <FormControlLabel
                              value={t("fulfilled")}
                              control={<Radio color="success" />}
                              label={t("fulfilled")}
                            />
                            <FormControlLabel
                              value={t("notFulfilled")}
                              control={<Radio color="secondary" />}
                              label={t("notFulfilled")}
                            />
                            <FormControlLabel
                              value={t("notRelevant")}
                              control={<Radio color="default" />}
                              label={t("notRelevant")}
                            />
                          </RadioGroup>
                        </FormControl>
                      )}
                    </Box>

                    {/* Rechte Box: Requirement Text + Gewicht */}
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "flex-start",
                        alignItems: "flex-start",
                        width: "75%",
                      }}
                    >
                      <List
                        sx={{
                          flex: 1,
                          listStyleType: "circle",
                          minWidth: 0,
                          p: 0,
                          ml: 2,
                        }}
                      >
                        {requirement.gradations ? (
                          [...(requirement.recommendations || [])]
                            .sort((a, b) => a.level - b.level)
                            .map((rec) => (
                              <Typography
                                key={rec.id}
                                sx={{
                                  ml: 1,
                                  color: requirement.irrelevant
                                    ? "#999999"
                                    : "#000000",
                                }}
                              >
                                {`${rec.level}: ${rec.description}`}
                              </Typography>
                            ))
                        ) : (
                          <Typography
                            sx={{
                              ml: 1,
                              color: requirement.irrelevant
                                ? "#999999"
                                : "#000000",
                            }}
                          >
                            {requirement.text}
                          </Typography>
                        )}
                      </List>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          flexDirection: "row",
                          flexShrink: 0,
                        }}
                      >
                        <Typography
                          sx={{
                            ml: 1,
                            whiteSpace: "nowrap",
                            color: requirement.irrelevant
                              ? "#999999"
                              : "#000000",
                          }}
                        >
                          {` (${t("weight")}: ${requirement.weight})`}
                        </Typography>
                      </Box>
                    </Box>
                  </ListItem>
                )
              )}
            </List>
          </Box>
        )}
      </Box>
      <Divider flexItem orientation="horizontal" sx={{ mt: 4, mx: 2 }} />
    </Box>
  );
}
